import { nextTick } from 'vue';
// @ts-ignore
import downloadFile from 'downloadjs';
import { ReportTypes } from '@/modules/Statistics/interfaces/ReportTypes';
import { downloadCSV as downloadCSVHelper, escapeCsvValue } from '@/helpers/PrintHelper';
import moment from 'moment';

const reportTypes = ReportTypes;

export interface DownloadReportData {
    reportId: string;
    name: string;
    reportData: any;
    fileType: string;
    reportType: string;
}

export interface DownloadReportOptions {
    refs: Record<string, any>;
    htmlToPaper: (elementId: string, options: { windowTitle: string }) => Promise<void>;
    translate: (key: string) => string;
    onExpand: (reportId: string) => void;
    onRevert: () => void;
}

// DOWNLOADING REPORTS

export function downloadChartIMG(
    data: { reportId: string; name: string; reportData: any; fileType: string },
    refs: Record<string, any>,
) {
    if (data.reportData.type === reportTypes.chart || data.reportData.type === reportTypes.pieChart) {
        // Get raw img data from chart and download it
        const chartRef = (refs[data.reportId] as any[])[0].$refs.chartRef;
        // @ts-ignore
        chartRef.dataURI().then(({ imgURI }) => {
            downloadFile(imgURI as string, data.name + '.png', 'image/png');
        });
    }
}

export function downloadCSV(data: DownloadReportData, refs: Record<string, any>) {
    let csvData = '';
    let titles = '';
    const reportRef = refs[data.reportId] as any;

    switch (data.reportType) {
        case reportTypes.projectsOverview:
            {
                const names = reportRef.$el.querySelectorAll('.projects-overview__status');
                const values = reportRef.$el.querySelectorAll('.projects-overview__value');

                // Concatenate names and values to csv string with proper escaping
                for (let i = 0; i < names.length; i++) {
                    const nameValue = escapeCsvValue(names[i].textContent || '');
                    const statusValue = escapeCsvValue(values[i].textContent || '');
                    csvData += `${nameValue};${statusValue}\n`;
                }

                downloadCSVHelper(csvData, data.name);
            }
            break;

        // CHARTS / PIE CHARTS
        case reportTypes.chart:
        case reportTypes.pieChart:
            {
                // Concatenate chart data to csv string with proper escaping
                const field0Name = escapeCsvValue(data.reportData.fields[0].name);
                const field1Name = escapeCsvValue(
                    data.reportData.fields[data.reportData.type === reportTypes.chart ? 2 : 1].name,
                );
                csvData += `${field0Name};${field1Name}\n`;

                for (let i = 0; i < data.reportData.values.length; i++) {
                    const value0 = escapeCsvValue(String(data.reportData.values[i][0] || ''));
                    const value1 = escapeCsvValue(String(data.reportData.values[i][1] || ''));
                    csvData += `${value0};${value1}\n`;
                }

                downloadCSVHelper(csvData, data.name);
            }
            break;

        // TABLES / MATRIX
        case reportTypes.matrix:
        case reportTypes.tableReport:
        case reportTypes.news:
            {
                // CONCATENATE TABLE DATA TO CSV STRING WITH PROPER ESCAPING
                const tableElement = Array.isArray(reportRef) ? reportRef[0].$refs.tableRef : reportRef.$refs.tableRef;
                const rows = tableElement.$el.querySelectorAll('tbody tr');
                for (let i = 0; i < rows.length; i++) {
                    const cells = rows[i].querySelectorAll('td');
                    const rowValues: string[] = [];
                    for (let j = 0; j < cells.length; j++) {
                        const cellValue = escapeCsvValue(cells[j].innerText || '');
                        rowValues.push(cellValue);
                    }
                    csvData += rowValues.join(';') + '\n';
                }

                // CONCATENATE TABLE HEADERS TO CSV STRING WITH PROPER ESCAPING
                const headerValues: string[] = [];
                for (let i = 0; i < tableElement.columns.length; i++) {
                    const headerValue = escapeCsvValue(tableElement.columns[i].title || '');
                    headerValues.push(headerValue);
                }
                titles = headerValues.join(';');
                csvData = titles + '\n' + csvData;

                downloadCSVHelper(csvData, data.name);
            }
            break;
    }
}

export async function downloadReport(data: DownloadReportData, options: DownloadReportOptions) {
    options.onExpand(data.reportId);
    await nextTick();

    switch (data.fileType) {
        case 'pdf':
            const reportName = data?.reportData?.title ?? data.reportId;
            await options.htmlToPaper(data.reportId === 'news' ? 'news-table' : data.reportId, {
                windowTitle: options.translate('Roltek dashboard') + ' - ' + reportName,
            });
            break;
        case 'png':
            downloadChartIMG(data, options.refs);
            break;
        case 'csv':
            downloadCSV(data, options.refs);
            break;
    }

    await nextTick();
    options.onRevert();
}

// DATA SERIALIZATION
export function serializeNewActivities(nextSteps: any, sidebarData: any) {
    for (const task of nextSteps.value) {
        sidebarData.value.newTasks.push({
            date: moment(task.dateFrom).format('DD.MM.YYYY'),
            time: moment(task.dateFrom).format('HH:mm'),
            name: task.lead?.project?.client?.name,
            status: task?.type?.name,
            projectId: task.lead?.project?.id,
        });
    }

    return Promise.resolve();
}

export function serializeNewComments(chatProjects: any, sidebarData: any) {
    for (let i = 0; i < 3; i++) {
        const project = chatProjects.value[i];

        sidebarData.value.newComments.push({
            title: project.projectInfo.clientName,
            user: project.projectInfo.createdBy,
            date: moment(project.projectInfo.createdAt).format('DD.MM.YYYY'),
            time: moment(project.projectInfo.createdAt).format('HH:mm'),
            text: project.projectInfo.latestMessage,
            projectId: project.projectInfo.projectId,
        });
    }
}
